// prefabTrigger — pure resolution of a Convorcher NewResponse trigger string
// to a DB-backed prefab row (ENG-834).
//
// Convorcher broadcasts the prefab *name* exactly as stored in its prefabs
// table (NormalizePrefabName keeps case + underscores, strips the rest — so
// "FundingFrustration" and "greeting" both arrive verbatim). GASS keys prefab
// rows by lower_snake_case `tag`. The bridge is one deterministic
// normalization — NOT a hand-maintained map (the old 7-entry PREFAB_TRIGGERS
// dictionary this module replaces was already stale: dead entries, and
// unmapped names silently no-oped).
//
// Contract: normalizeTriggerToTag(convorcherName) === the GASS tag, for any
// prefab authored under the ENG-834 convention (new prefabs: name == tag
// already; legacy PascalCase names: deterministic PascalCase → snake_case).
// Pure functions, no I/O — unit-tested in tests/unit/prefabTrigger.test.mjs.

/**
 * Deterministically convert a Convorcher trigger/prefab name to a GASS tag.
 *   "FundingFrustration" → "funding_frustration"
 *   "greeting"           → "greeting"   (already canonical — unchanged)
 *   "back_to_work"       → "back_to_work"
 *   "$BackToWork"        → "back_to_work"  (mangled-LLM chars stripped, same
 *                                           charset rule as convorcher's
 *                                           NormalizePrefabName)
 *   "ABCWord"            → "abc_word"   (acronym runs split before last cap)
 */
export function normalizeTriggerToTag(raw) {
  const cleaned = String(raw ?? '').replace(/[^A-Za-z0-9_]/g, '')
  if (!cleaned) return ''
  return cleaned
    // split acronym runs: "ABCWord" → "ABC_Word"
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    // split lower/digit → upper boundaries: "FundingFrustration" → "Funding_Frustration"
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '')
}

/**
 * Resolve an incoming trigger against the character's loaded prefab rows.
 * Only `status === 'ready'` rows qualify — mirrors the runtime-registration
 * filter in CustomAssetManager (a draft prefab must not fire in conversation).
 *
 * Match order: exact tag equality first (fast path, canonical authoring),
 * then normalized-tag equality (tolerates a tag authored with stray casing).
 * Returns the prefab row or null. Never throws on malformed input.
 */
export function resolvePrefabForTrigger(trigger, prefabs) {
  const tag = normalizeTriggerToTag(trigger)
  if (!tag) return null
  const list = Array.isArray(prefabs) ? prefabs : []
  return (
    list.find(p => p?.status === 'ready' && p?.tag === tag) ??
    list.find(p => p?.status === 'ready' && normalizeTriggerToTag(p?.tag) === tag) ??
    null
  )
}
